import React, { ChangeEvent } from "react";
import { styled, borderRadius, fontSizes, cardShadow, media } from "@iventis/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ProgressStep, SelectedStepOption } from "./sign-up-questionnaire";

interface TrialQuestionTileProps {
    step: ProgressStep;
    stepOption: ProgressStep["content"]["options"][0];
    onChange: (event: ChangeEvent<HTMLInputElement>, choice: SelectedStepOption) => void;
    checked: boolean;
}

const TileIcon = ({ icon, checked }) => (
    <StyledTileIconWrapper isChecked={checked}>
        <FontAwesomeIcon icon={[checked ? "fas" : "far", icon]} />
    </StyledTileIconWrapper>
);

export const TrialQuestionTile: React.FunctionComponent<TrialQuestionTileProps> = ({ step, stepOption, onChange, checked = false }) => (
    <StyledLabel htmlFor={`option-${stepOption.id}`} isChecked={checked} stepName={step.name} data-testid={stepOption.id}>
        {stepOption.icon && <TileIcon checked={checked} icon={stepOption.icon} />}
        <StyledInput
            type="radio"
            name={stepOption.name}
            id={`option-${stepOption.id}`}
            value={stepOption.name}
            onChange={(e) => onChange(e, { step: step.id, answer: stepOption.id })}
            checked={checked}
        />
        <StyledSpan>{stepOption.name}</StyledSpan>
    </StyledLabel>
);

const StyledLabel = styled("label")<{ isChecked: boolean; stepName: string }>`
    box-sizing: border-box;
    margin: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: all 0.2s ease-in-out;
    padding: 20px;
    border: ${(props) => `1px solid ${props.isChecked ? props.theme.primaryColors.subdued70 : props.theme.shades.darkBorder}`};
    width: 150px;
    height: 160px;
    cursor: pointer;
    border-radius: ${borderRadius.double};
    font-size: ${fontSizes.small};
    color: ${(props) => props.theme.primaryColors.subdued70};
    box-shadow: ${(props) => (props.isChecked ? cardShadow : "none")};
    background: ${(props) => (props.isChecked ? props.theme.tertiaryColors.primaryBackground : "transparent")};
    ${media.medium} {
        width: ${(props) => `${props.stepName === "step-2" ? "181px" : "200px"}`};
        height: ${(props) => `${props.stepName === "step-2" ? "150px" : "200px"}`};
        font-size: ${fontSizes.large};
    }
`;

const StyledTileIconWrapper = styled("div")<{ isChecked: boolean }>(({ theme, isChecked }) => ({
    transition: "all 0.2s ease-in-out",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxSizing: "border-box",
    padding: "25px",
    border: `2px solid ${isChecked ? theme.shades.five : theme.primaryColors.subduedMono}`,
    background: isChecked ? theme.primaryColors.subdued70 : "transparent",
    borderRadius: "100%",
    maxHeight: "60px",
    maxWidth: "60px",
    alignSelf: "flex-start",
    margin: "0 auto 10px auto",
    boxShadow: isChecked ? cardShadow : "none",
    color: isChecked ? theme.shades.five : theme.primaryColors.subdued70,
}));

const StyledInput = styled("input")(({ theme }) => ({
    appearance: "none",
    /* For iOS < 15 to remove gradient background */
    backgroundColor: theme.shades.five,
    /* Not removed via appearance */
    margin: 0,
}));

const StyledSpan = styled.span`
    display: flex;
    flex: 1 1 100%;
    align-items: center;
    justify-content: center;
`;

export default TrialQuestionTile;
